/**
 * AdminEvents Page Component
 * Comprehensive management dashboard for creating, editing, and deleting events,
 * reviewing user ticket booking requests, managing category content, and auditing actions.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { fetchJsonWithBackendFallback, fetchWithBackendFallback } from '../../utils/backendApi';
import { appendAuditLogEntry } from '../../utils/auditLog';
import '../../assets/fontello-plus/fontello-73784b09/css/calander.css';
import '../../assets/fontello-change/fontello-79be5077/css/fontello.css';

// Event categories — 'social' entries are shown on the Social page Upcoming Events section. ("events")
const CATEGORIES = [
  { value: 'community', label: 'Our Sub Groups' },
  { value: 'sports', label: 'Sport Activities' },
  { value: 'activities', label: 'Community Activities and Groups' },
  { value: 'social', label: 'Social Events' },
];
const AUTO_EXPIRE_DAYS = 14;
const CATEGORY_IMAGE_STORE_KEY = 'clipstone_category_content_images';
const EVENT_LINK_STORE_KEY = 'clipstone_event_links';
const CATEGORY_LINK_STORE_KEY = 'clipstone_category_content_links';

const AdminEvents = () => {
  const navigate = useNavigate();

  const [events, setEvents]           = useState([]);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [bookingLoading, setBookingLoading] = useState(true);
  const [contentItems, setContentItems] = useState([]);
  const [contentLoading, setContentLoading] = useState(true);
  const [contentImageFiles, setContentImageFiles] = useState([]);
  const [contentImagePreviews, setContentImagePreviews] = useState([]);
  const [error, setError]             = useState('');
  const [saving, setSaving]           = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [bookingDeleteId, setBookingDeleteId] = useState(null);
  const [contentDeleteId, setContentDeleteId] = useState(null);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [contentForm, setContentForm] = useState({ category: 'community', title: '', description: '', details: '', status: 'Hosted', image_url: '', image_urls: [], trustee_id: '', website_url: '' });
  const [contentEditingId, setContentEditingId] = useState(null);
  const [trustees, setTrustees] = useState([]);
  const [eventDrafts, setEventDrafts] = useState([]);
  const [contentDrafts, setContentDrafts] = useState([]);
  const [publishConfirm, setPublishConfirm] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [activeDraftSection, setActiveDraftSection] = useState('event');
  const [activeBookingSection, setActiveBookingSection] = useState('trust');
  const [adminEmail, setAdminEmail] = useState('');
  const [eventLinkMap, setEventLinkMap] = useState({});
  const [categoryLinkMap, setCategoryLinkMap] = useState({});
  const [draftDeleteConfirm, setDraftDeleteConfirm] = useState(null);
  const [imageDeleteConfirm, setImageDeleteConfirm] = useState(null);

  const [form, setForm] = useState({
    title: '', description: '', event_date: '', start_time: '',
    end_time: '', location: '', category: 'community', event_url: '',
  });
  const [imageFile, setImageFile]       = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Returns the set of booking request IDs that an admin has manually marked to keep
  // beyond the auto-expire window (stored in localStorage). ("bookings")
  function getKeptBookingRequestIds() {
    if (typeof window === 'undefined') return [];

    try {
      const stored = window.localStorage.getItem('keptBookingRequestIds');
      return stored ? JSON.parse(stored) : [];
    } catch (err) {
      console.error('Unable to load kept booking requests:', err);
      return [];
    }
  }

  // Strips special characters and normalises spacing for fuzzy title matching
  // between event names and category content titles. ("category-content")
  const normalizeTitleForMatch = (value) =>
    value?.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim() || '';

  // Lower-cases and trims a booking field for consistent comparison. ("bookings")
  const normalizeBookingText = (value) =>
    String(value || '').toLowerCase().trim();

  // Extracts the embedded receipt JSON from a booking's notes field.
  // The receipt is serialised as "RECEIPT_JSON:{...}" at the end of the notes string. ("bookings")
  const getRentalReceiptMeta = (notes) => {
    const text = String(notes || '');
    const marker = 'RECEIPT_JSON:';
    const markerIndex = text.indexOf(marker);

    if (markerIndex === -1) {
      return { receipt: null, cleanNotes: text.trim() };
    }

    const before = text.slice(0, markerIndex).trim();
    const after = text.slice(markerIndex + marker.length).trim();

    try {
      return {
        receipt: JSON.parse(after),
        cleanNotes: before,
      };
    } catch (err) {
      return {
        receipt: null,
        cleanNotes: text.trim(),
      };
    }
  };

  const getBookingSection = (request) => {
    const sourceHint = normalizeBookingText(
      request?.booking_section || request?.booking_type || request?.request_type || request?.source || request?.category
    );
    const descriptor = normalizeBookingText(`${request?.group_name || ''} ${request?.event_title || ''}`);

    if (sourceHint.includes('social')) return 'social';
    if (sourceHint.includes('trust')) return 'trust';
    if (descriptor.includes('social')) return 'social';

    // Default existing requests to trust so current behaviour remains stable.
    return 'trust';
  };

  const trustBookingRequests = bookingRequests.filter((request) => getBookingSection(request) === 'trust');
  const socialBookingRequests = bookingRequests.filter((request) => getBookingSection(request) === 'social');

  // Social event attendance requests — from "Request to attend" on the Social page upcoming events. ("social-booking")
  const socialEventRequests = socialBookingRequests.filter(
    (r) => normalizeBookingText(r.booking_type || '') === 'social-event'
  );
  // Venue requests: room-hire form submissions + pre-booking enquiries, shown together. ("social-booking")
  const venueRequests = socialBookingRequests.filter(
    (r) => normalizeBookingText(r.booking_type || '') !== 'social-event'
  );

  const visibleBookingRequests =
    activeBookingSection === 'venue'         ? venueRequests :
    activeBookingSection === 'social-events' ? socialEventRequests :
    trustBookingRequests;
  const todayIso = new Date().toISOString().slice(0, 10);
  const upcomingSocialEvents = events
    .filter((event) =>
      normalizeBookingText(event?.category) === 'social' &&
      String(event?.event_date || '') >= todayIso
    )
    .sort((a, b) => String(a.event_date || '').localeCompare(String(b.event_date || '')));

  const getCategoryLabel = (value) => {
    const match = CATEGORIES.find((category) => category.value === value);
    return match?.label || value || 'Uncategorized';
  };

  const getStoredDrafts = (storageKey) => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = window.localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (err) {
      console.error('Unable to load saved drafts:', err);
      return [];
    }
  };

  const saveStoredDrafts = (storageKey, drafts) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, JSON.stringify(drafts));
  };

  const getStoredCategoryImageMap = () => {
    if (typeof window === 'undefined') return {};
    try {
      const stored = window.localStorage.getItem(CATEGORY_IMAGE_STORE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (err) {
      console.error('Unable to load stored category image map:', err);
      return {};
    }
  };

  const saveStoredCategoryImageMap = (imageMap) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(CATEGORY_IMAGE_STORE_KEY, JSON.stringify(imageMap));
  };

  const normalizeImageList = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string' && value.trim()) return [value.trim()];
    return [];
  };

  const getStoredMap = (key) => {
    if (typeof window === 'undefined') return {};

    try {
      const stored = window.localStorage.getItem(key);
      const parsed = stored ? JSON.parse(stored) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (err) {
      console.error(`Unable to parse local storage map for ${key}:`, err);
      return {};
    }
  };

  const saveStoredMap = (key, value) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, JSON.stringify(value || {}));
  };

  // Appends https:// to a URL if it has no scheme, so links stored without a protocol
  // still work as proper hyperlinks when rendered in the browser. ("category-content")
  const normalizeWebsiteUrl = (value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return '';

    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    return `https://${trimmed}`;
  };

  // A booking request is considered expired when it's older than AUTO_EXPIRE_DAYS (14 days)
  // and the admin hasn't explicitly marked it to keep. Expired requests are silently deleted
  // from the backend on the next fetch cycle. ("bookings")
  function isBookingRequestExpired(request) {
    if (!request?.created_at) return false;

    const keptIds = getKeptBookingRequestIds();
    if (keptIds.includes(request.id)) {
      return false;
    }

    const createdAt = new Date(request.created_at);
    if (Number.isNaN(createdAt.getTime())) return false;

    const expiresAt = new Date(createdAt);
    expiresAt.setDate(expiresAt.getDate() + AUTO_EXPIRE_DAYS);

    return new Date() >= expiresAt;
  }

  // NOTE: was using raw, unauthenticated fetch() against an admin-gated
  // endpoint (verify_admin_token in booking.py). Switched to
  // fetchJsonWithBackendFallback / fetchWithBackendFallback with
  // requireAuth: true so the current Supabase session's access token is
  // sent as a Bearer token. Without this, the request was rejected before
  // it ever reached the route handler, and the catch block below silently
  // reset the list to [] — which is why bookings never appeared.
  const fetchBookingRequests = async (showLoading = false) => {
    if (showLoading) setBookingLoading(true);
    try {
      const payload = await fetchJsonWithBackendFallback('/bookings/event-requests', {
        requireAuth: true,
      });
      const requestList = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.value)
          ? payload.value
          : [];

      const expiredRequests = requestList.filter(isBookingRequestExpired);
      if (expiredRequests.length > 0) {
        await Promise.all(
          expiredRequests.map((request) =>
            fetchWithBackendFallback(`/bookings/event-requests/${request.id}`, {
              method: 'DELETE',
              requireAuth: true,
            })
          )
        );
      }

      setBookingRequests(requestList.filter((request) => !isBookingRequestExpired(request)));
    } catch (err) {
      console.error('Booking request fetch error:', err);
      setBookingRequests([]);
    } finally {
      if (showLoading) setBookingLoading(false);
    }
  };

  // Toggles a booking request's "keep" flag in localStorage.
  // Kept requests are excluded from the auto-expire logic so admins can retain important ones. ("bookings")
  const toggleKeepBookingRequest = (requestId) => {
    if (typeof window === 'undefined') return;

    const keptIds = new Set(getKeptBookingRequestIds());

    if (keptIds.has(requestId)) {
      keptIds.delete(requestId);
    } else {
      keptIds.add(requestId);
    }

    window.localStorage.setItem('keptBookingRequestIds', JSON.stringify([...keptIds]));
    fetchBookingRequests();
  };

  // Sends an authenticated DELETE request to the backend to permanently remove
  // a booking request — requireAuth ensures the admin session token is included. ("bookings")
  const deleteBookingRequest = async (requestId) => {
    try {
      await fetchWithBackendFallback(`/bookings/event-requests/${requestId}`, {
        method: 'DELETE',
        requireAuth: true,
      });

      setBookingDeleteId(null);
      await fetchBookingRequests();
    } catch (err) {
      console.error('Booking request delete error:', err);
      setError(err.message || 'Could not delete booking request.');
    }
  };

  // ── Guard: must be logged in AND be an admin ─────────────────────────────────
  // Checks the Supabase session and calls the check_is_admin RPC function.
  // Non-admins and unauthenticated users are redirected to /login. ("admin-auth")
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          navigate('/login');
          return;
        }

        const { data: isAdmin, error: rpcError } = await supabase.rpc(
          'check_is_admin',
          { p_email: session.user.email }
        );

        if (rpcError) {
          console.error('Admin access check failed:', rpcError);
          setError('Unable to verify admin access right now. Please refresh or try again.');
          return;
        }

        if (isAdmin === false) {
          setError('You do not have permission to access the admin area.');
          return;
        }

        if (isAdmin == null) {
          setError('Unable to verify admin access right now.');
          return;
        }

        setAdminEmail(session.user.email || '');

        fetchEvents();
        fetchBookingRequests(true);
        fetchCategoryContent();
      } catch (err) {
        console.error('Access check error:', err);
        navigate('/login');
      }
    };

    checkAccess();

    const handleBookingRequestRefresh = () => {
      fetchBookingRequests(true);
    };

    window.addEventListener('booking-request-updated', handleBookingRequestRefresh);

    // Poll for new booking requests every 30 seconds so the admin list stays
    // fresh without a manual page refresh. ("bookings")
    const cleanupInterval = window.setInterval(() => {
      fetchBookingRequests();
    }, 30000);

    return () => {
      window.removeEventListener('booking-request-updated', handleBookingRequestRefresh);
      window.clearInterval(cleanupInterval);
    };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch all events ──────────────────────────────────────────────────────────
  // Queries tbl_events via Supabase directly (no backend fallback needed here
  // because the admin has an active Supabase session). ("events")
  const fetchEvents = async () => {
    setLoading(true);
    const { data, error: fetchError } = await supabase
      .from('tbl_events')
      .select('*')
      .order('event_date', { ascending: true });

    if (fetchError) {
      setError('Could not load events.');
      console.error(fetchError);
    } else {
      setEvents(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    const storedTrustees = window.localStorage.getItem('clipstone_trustees');
    if (storedTrustees) {
      try {
        setTrustees(JSON.parse(storedTrustees));
      } catch (err) {
        console.error('Unable to load trustees for admin events:', err);
      }
    }
  }, []);

  // Reads saved event and category content drafts from localStorage on mount.
  // Also loads the link maps so stored URLs survive page refreshes. ("drafts")
  useEffect(() => {
    setEventDrafts(getStoredDrafts('clipstone_event_drafts'));
    setContentDrafts(getStoredDrafts('clipstone_category_content_drafts'));
    setEventLinkMap(getStoredMap(EVENT_LINK_STORE_KEY));
    setCategoryLinkMap(getStoredMap(CATEGORY_LINK_STORE_KEY));
  }, []);

  // Fetches all category content items from the backend, auto-syncs each item's "Hosted"
  // status by matching its title against live events, then merges stored image/URL maps.
  // Falls back to localStorage if the backend is unreachable. ("category-content")
  const fetchCategoryContent = async () => {
    setContentLoading(true);
    try {
      // GET is intentionally public in booking.py, so no requireAuth here.
      const data = await fetchJsonWithBackendFallback('/bookings/category-content');
      const liveEvents = Array.isArray(events) ? events : [];

      const syncedItems = Array.isArray(data)
        ? data.map((item) => {
            const matchesEvent = liveEvents.some((event) => {
              const normalizedEventTitle = normalizeTitleForMatch(event?.title);
              const normalizedItemTitle = normalizeTitleForMatch(item?.title);
              return Boolean(normalizedEventTitle) && Boolean(normalizedItemTitle) && (
                normalizedEventTitle === normalizedItemTitle ||
                normalizedEventTitle.includes(normalizedItemTitle) ||
                normalizedItemTitle.includes(normalizedEventTitle)
              );
            });

            return {
              ...item,
              status: matchesEvent ? 'Hosted' : 'Not currently hosted',
            };
          })
        : [];

      // NOTE: this PUT hits the admin-gated /bookings/category-content/{id}
      // route (verify_admin_token in booking.py) — requireAuth added so the
      // sync no longer fails silently against every item.
      await Promise.all(
        syncedItems.map((item) =>
          fetchWithBackendFallback(`/bookings/category-content/${item.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              category: item.category || 'community',
              title: item.title || '',
              description: item.description || null,
              details: item.details || null,
              status: item.status || 'Not currently hosted',
              image_url: item.image_url || null,
              trustee_id: item.trustee_id || null,
            }),
            requireAuth: true,
          })
        )
      );

      const storedImageMap = getStoredCategoryImageMap();
      const mergedItems = syncedItems.map((item) => {
        const imageUrls = normalizeImageList(storedImageMap[item.id] || item.image_urls || item.image_url);
        return {
          ...item,
          image_urls: imageUrls,
          image_url: imageUrls[0] || null,
          website_url: item.website_url || categoryLinkMap[item.id] || '',
        };
      });

      setContentItems(mergedItems);
      window.localStorage.setItem('clipstone_category_content', JSON.stringify(mergedItems));
    } catch (err) {
      console.error('Category content fetch error:', err);
      // Fall back to localStorage if backend is unavailable
      const storedContent = window.localStorage.getItem('clipstone_category_content');
      if (storedContent) {
        try {
          const parsedContent = JSON.parse(storedContent);
          const normalized = Array.isArray(parsedContent)
            ? parsedContent.map((item) => {
                const imageUrls = normalizeImageList(item.image_urls || item.image_url);
                return {
                  ...item,
                  image_urls: imageUrls,
                  image_url: imageUrls[0] || null,
                  website_url: item.website_url || categoryLinkMap[item.id] || '',
                };
              })
            : [];
          setContentItems(normalized);
        } catch (parseErr) {
          console.error('Unable to parse stored content:', parseErr);
          setContentItems([]);
        }
      } else {
        setContentItems([]);
      }
    } finally {
      setContentLoading(false);
    }
  };


  useEffect(() => {
    fetchCategoryContent();
  }, [events, categoryLinkMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetContentForm = () => {
    setContentForm({ category: 'community', title: '', description: '', details: '', status: 'Hosted', image_url: '', image_urls: [], trustee_id: '', website_url: '' });
    setContentImageFiles([]);
    setContentImagePreviews([]);
    setContentEditingId(null);
    setImageDeleteConfirm(null);
  };

  const loadEventDraftIntoForm = (draft) => {
    setForm({
      title: draft.title || '',
      description: draft.description || '',
      event_date: draft.event_date || '',
      start_time: draft.start_time || '',
      end_time: draft.end_time || '',
      location: draft.location || '',
      category: draft.category || 'community',
      event_url: draft.event_url || '',
    });
    setImageFile(null);
    setImagePreview(null);
    setEditingId(null);
    setError('');
    setStatusMessage('Event draft loaded into the form.');
    window.setTimeout(() => setStatusMessage(''), 4000);
  };

  const loadContentDraftIntoForm = (draft) => {
    setContentForm({
      category: draft.category || 'community',
      title: draft.title || '',
      description: draft.description || '',
      details: draft.details || '',
      status: draft.status || 'Hosted',
      image_url: draft.image_url || '',
      image_urls: normalizeImageList(draft.image_urls || draft.image_url),
      trustee_id: draft.trustee_id || '',
      website_url: draft.website_url || '',
    });
    setContentImageFiles([]);
    setContentImagePreviews(normalizeImageList(draft.image_urls || draft.image_url));
    setContentEditingId(null);
    setError('');
    setStatusMessage('Activity draft loaded into the form.');
    window.setTimeout(() => setStatusMessage(''), 4000);
  };

  const deleteStoredEventDraft = (draftId) => {
    const updatedDrafts = eventDrafts.filter((draft) => draft.id !== draftId);
    saveStoredDrafts('clipstone_event_drafts', updatedDrafts);
    setEventDrafts(updatedDrafts);
    setDraftDeleteConfirm(null);
    setStatusMessage('Event draft deleted.');
    window.setTimeout(() => setStatusMessage(''), 4000);
  };

  const deleteStoredContentDraft = (draftId) => {
    const updatedDrafts = contentDrafts.filter((draft) => draft.id !== draftId);
    saveStoredDrafts('clipstone_category_content_drafts', updatedDrafts);
    setContentDrafts(updatedDrafts);
    setDraftDeleteConfirm(null);
    setStatusMessage('Activity draft deleted.');
    window.setTimeout(() => setStatusMessage(''), 4000);
  };

  // Saves or updates a category content item to both the backend and localStorage.
  // Auto-sets the "Hosted" status by checking if a matching event exists in tbl_events.
  // Also records an audit log entry for every change. ("category-content")
  const saveCategoryContent = async (e = null) => {
    if (e?.preventDefault) e.preventDefault();
    setPublishConfirm(null);
    setStatusMessage('');
    try {
      const existingImages = normalizeImageList(contentForm.image_urls || contentForm.image_url);
      const uploadedImageUrls = contentImageFiles.length > 0
        ? await Promise.all(contentImageFiles.map((file) => uploadImageFile(file)))
        : [];
      const imageUrls = [...existingImages, ...uploadedImageUrls].filter(Boolean);
      const primaryImageUrl = imageUrls[0] || null;

      const autoHostedStatus = events.some((event) => {
        const normalizedEventTitle = normalizeTitleForMatch(event?.title);
        const normalizedItemTitle = normalizeTitleForMatch(contentForm.title);
        return Boolean(normalizedEventTitle) && Boolean(normalizedItemTitle) && (
          normalizedEventTitle === normalizedItemTitle ||
          normalizedEventTitle.includes(normalizedItemTitle) ||
          normalizedItemTitle.includes(normalizedEventTitle)
        );
      })
        ? 'Hosted'
        : 'Not currently hosted';

      const method = contentEditingId ? 'PUT' : 'POST';
      const endpoint = contentEditingId
        ? `/bookings/category-content/${contentEditingId}`
        : '/bookings/category-content';

      const payload = {
        ...contentForm,
        status: autoHostedStatus,
        image_url: primaryImageUrl,
        image_urls: imageUrls,
        trustee_id: contentForm.trustee_id || null,
        website_url: normalizeWebsiteUrl(contentForm.website_url),
      };

      const backendPayload = {
        category: payload.category,
        title: payload.title,
        description: payload.description,
        details: payload.details,
        status: payload.status,
        image_url: payload.image_url,
        trustee_id: payload.trustee_id,
      };

      // NOTE: POST and PUT here are both admin-gated in booking.py
      // (verify_admin_token) — requireAuth added so this stops silently
      // falling back to localStorage-only saves.
      let backendSuccess = false;
      try {
        const response = await fetchWithBackendFallback(endpoint, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(backendPayload),
          requireAuth: true,
        });
        backendSuccess = response.ok;
      } catch (fetchErr) {
        console.warn('Backend fetch failed, will save to localStorage:', fetchErr);
      }

      const isUpdatingContent = Boolean(contentEditingId);
      const updatedItems = contentEditingId
        ? contentItems.map((item) =>
            item.id === contentEditingId
              ? { id: contentEditingId, ...payload }
              : item
          )
        : [{ id: Date.now().toString(), ...payload }, ...contentItems];

      const nextImageMap = getStoredCategoryImageMap();
      const nextLinkMap = getStoredMap(CATEGORY_LINK_STORE_KEY);
      updatedItems.forEach((item) => {
        nextImageMap[item.id] = normalizeImageList(item.image_urls || item.image_url);
        if (item.website_url) {
          nextLinkMap[item.id] = item.website_url;
        } else {
          delete nextLinkMap[item.id];
        }
      });
      saveStoredCategoryImageMap(nextImageMap);
      saveStoredMap(CATEGORY_LINK_STORE_KEY, nextLinkMap);
      setCategoryLinkMap(nextLinkMap);

      setContentItems(updatedItems);
      window.localStorage.setItem('clipstone_category_content', JSON.stringify(updatedItems));
      resetContentForm();
      const successMessage = isUpdatingContent
        ? 'Activity information has been updated.'
        : 'Activity information has been added.';
      setStatusMessage(successMessage);
      window.setTimeout(() => setStatusMessage(''), 4000);

      appendAuditLogEntry({
        actorEmail: adminEmail,
        action: isUpdatingContent ? 'Updated' : 'Added',
        entityType: 'information',
        entityName: payload.title || 'Activity information',
        details: `${isUpdatingContent ? 'Updated' : 'Added'} category content in ${payload.category || 'community'} section.`,
      });

      if (!backendSuccess) {
        console.warn('Saved to local storage (backend may be unavailable)');
      }
    } catch (err) {
      console.error('Content save error:', err);
      try {
        const fallbackItems = contentEditingId
          ? contentItems.map((item) =>
              item.id === contentEditingId
                ? {
                    id: contentEditingId,
                    ...contentForm,
                    image_urls: normalizeImageList(contentForm.image_urls || contentForm.image_url),
                    image_url: normalizeImageList(contentForm.image_urls || contentForm.image_url)[0] || null,
                  }
                : item
            )
          : [{
              id: Date.now().toString(),
              ...contentForm,
              image_urls: normalizeImageList(contentForm.image_urls || contentForm.image_url),
              image_url: normalizeImageList(contentForm.image_urls || contentForm.image_url)[0] || null,
            }, ...contentItems];
        setContentItems(fallbackItems);
        window.localStorage.setItem('clipstone_category_content', JSON.stringify(fallbackItems));
        setError('Saved to local storage (backend unavailable)');
      } catch (storageErr) {
        setError(err.message || 'Could not save category content.');
      }
    }
  };

  // Saves the current category content form state as a local draft in localStorage
  // without publishing it to the backend — useful for incomplete activity listings. ("drafts")
  const saveCategoryContentDraft = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setPublishConfirm(null);
    setStatusMessage('');
    const draftEntry = {
      id: `draft-content-${Date.now()}`,
      category: contentForm.category || 'community',
      title: contentForm.title?.trim() || 'Untitled activity',
      description: contentForm.description?.trim() || '',
      details: contentForm.details?.trim() || '',
      status: 'Draft',
      image_url: contentForm.image_url || null,
      image_urls: normalizeImageList(contentForm.image_urls || contentForm.image_url),
      trustee_id: contentForm.trustee_id || null,
      website_url: normalizeWebsiteUrl(contentForm.website_url),
      created_at: new Date().toISOString(),
    };
    const updatedDrafts = [draftEntry, ...contentDrafts.filter((draft) => draft.id !== draftEntry.id)];
    saveStoredDrafts('clipstone_category_content_drafts', updatedDrafts);
    setContentDrafts(updatedDrafts);
    resetContentForm();
    setStatusMessage('Activity information saved as a draft locally.');
  };

  const promptCategoryContentPublish = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setPublishConfirm({ type: 'content' });
  };

  // Populates the category content form with an existing item's data
  // so the admin can edit and re-publish it. ("category-content")
  const startEditContent = (item) => {
    setContentEditingId(item.id);
    setContentForm({
      category: item.category || 'community',
      title: item.title || '',
      description: item.description || '',
      details: item.details || '',
      status: item.status || 'Hosted',
      image_url: item.image_url || '',
      image_urls: normalizeImageList(item.image_urls || item.image_url),
      trustee_id: item.trustee_id || '',
      website_url: item.website_url || categoryLinkMap[item.id] || '',
    });
    setContentImagePreviews(normalizeImageList(item.image_urls || item.image_url));
    setContentImageFiles([]);
    setStatusMessage('');
  };

  // Deletes a category content item from the backend and removes it from local
  // state and localStorage, also cleaning up its stored image and link map entries. ("category-content")
  const deleteCategoryContent = async (itemId) => {
    try {
      // Try to delete from backend
      // NOTE: admin-gated (verify_admin_token in booking.py) — requireAuth added.
      try {
        const response = await fetchWithBackendFallback(`/bookings/category-content/${itemId}`, {
          method: 'DELETE',
          requireAuth: true,
        });
        if (!response.ok) console.warn('Backend delete failed');
      } catch (fetchErr) {
        console.warn('Backend delete failed, removing from local storage only:', fetchErr);
      }

      // Delete from local state and localStorage
      const updatedItems = contentItems.filter((item) => item.id !== itemId);
      setContentItems(updatedItems);
      window.localStorage.setItem('clipstone_category_content', JSON.stringify(updatedItems));
      const nextImageMap = getStoredCategoryImageMap();
      const nextLinkMap = getStoredMap(CATEGORY_LINK_STORE_KEY);
      delete nextImageMap[itemId];
      delete nextLinkMap[itemId];
      saveStoredCategoryImageMap(nextImageMap);
      saveStoredMap(CATEGORY_LINK_STORE_KEY, nextLinkMap);
      setCategoryLinkMap(nextLinkMap);
      setContentDeleteId(null);
      appendAuditLogEntry({
        actorEmail: adminEmail,
        action: 'Deleted',
        entityType: 'information',
        entityName: contentItems.find((item) => item.id === itemId)?.title || 'Activity information',
        details: 'Removed category content item from admin content manager.',
      });
    } catch (err) {
      console.error('Content delete error:', err);
      setError(err.message || 'Could not delete category content.');
    }
  };

  // ── Reset form ────────────────────────────────────────────────────────────────
  // Clears all event form fields and resets the image/edit state back to "create new" mode. ("events")
  const resetForm = () => {
    setForm({
      title: '', description: '', event_date: '', start_time: '',
      end_time: '', location: '', category: 'community', event_url: '',
    });
    setImageFile(null);
    setImagePreview(null);
    setEditingId(null);
    setError('');
    setImageDeleteConfirm(null);
  };

  const startSocialUpcomingEvent = () => {
    resetForm();
    setForm((prev) => ({ ...prev, category: 'social' }));
  };

  // ── Handle image file selection ───────────────────────────────────────────────
  // Validates file type and size (max 5MB) before creating a local preview URL. ("events")
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPG, PNG, WEBP, or GIF).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB.');
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setError('');
    setImageDeleteConfirm(null);
  };

  const handleContentImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

    const invalidType = files.find((file) => !allowedTypes.includes(file.type));
    if (invalidType) {
      setError('Please select only valid image files (JPG, PNG, WEBP, or GIF).');
      return;
    }

    const overSize = files.find((file) => file.size > 5 * 1024 * 1024);
    if (overSize) {
      setError('Each image must be under 5MB.');
      return;
    }

    const nextFiles = [...contentImageFiles, ...files];
    const nextPreviewUrls = nextFiles.map((file) => URL.createObjectURL(file));

    setContentImageFiles(nextFiles);
    setContentImagePreviews(nextPreviewUrls);
    setError('');
    setImageDeleteConfirm(null);
  };

  // ── Upload image to Supabase Storage ─────────────────────────────────────────
  // Uploads a local File object to the "event-images" Supabase storage bucket
  // and returns the generated public URL for use in the event or content record. ("supabase")
  const uploadImageFile = async (file) => {
    if (!file) return null;

    const fileExt  = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('event-images')
      .upload(fileName, file, { cacheControl: '3600', upsert: false });

    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage
      .from('event-images')
      .getPublicUrl(fileName);

    return urlData.publicUrl;
  };

  // ── Create or update event ────────────────────────────────────────────────────
  // Shows the publish confirmation modal before committing the event to the database. ("events")
  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setPublishConfirm({ type: 'event' });
  };

  // ── Publish event (create or update) ─────────────────────────────────────────
  // Uploads the image (if provided), builds the payload, then calls Supabase to
  // either INSERT a new event or UPDATE the existing one identified by editingId.
  // Also records an audit log entry for every change. ("events")
  const publishEvent = async () => {
    setPublishConfirm(null);
    setError('');
    setStatusMessage('');

    if (!form.title.trim()) {
      setError('Event title is required.');
      return;
    }
    if (!form.event_date) {
      setError('Event date is required.');
      return;
    }

    const isUpdatingEvent = Boolean(editingId);
    setSaving(true);
    try {
      let imageUrl = null;
      if (imageFile) {
        imageUrl = await uploadImageFile(imageFile);
      }

      const payload = {
        title:       form.title.trim(),
        description: form.description.trim() || null,
        event_date:  form.event_date,
        start_time:  form.start_time || null,
        end_time:    form.end_time   || null,
        location:    form.location.trim() || null,
        category:    form.category,
        ...(imageUrl && { image_url: imageUrl }),
      };

      const normalizedEventUrl = normalizeWebsiteUrl(form.event_url);
      let savedEventId = editingId || null;

      if (editingId) {
        const { error: updateError } = await supabase
          .from('tbl_events')
          .update(payload)
          .eq('id', editingId);

        if (updateError) throw updateError;
      } else {
        const { data: insertedRows, error: insertError } = await supabase
          .from('tbl_events')
          .insert([payload])
          .select('id');

        if (insertError) throw insertError;
        savedEventId = insertedRows?.[0]?.id || null;
      }

      const nextEventLinkMap = getStoredMap(EVENT_LINK_STORE_KEY);
      if (savedEventId) {
        if (normalizedEventUrl) {
          nextEventLinkMap[savedEventId] = normalizedEventUrl;
        } else {
          delete nextEventLinkMap[savedEventId];
        }
        saveStoredMap(EVENT_LINK_STORE_KEY, nextEventLinkMap);
        setEventLinkMap(nextEventLinkMap);
      }

      resetForm();
      await fetchEvents();
      await fetchCategoryContent();
      const successMessage = isUpdatingEvent ? 'Event has been updated.' : 'Event has been added.';
      setStatusMessage(successMessage);
      window.setTimeout(() => setStatusMessage(''), 4000);
      appendAuditLogEntry({
        actorEmail: adminEmail,
        action: isUpdatingEvent ? 'Updated' : 'Added',
        entityType: 'event',
        entityName: payload.title,
        details: `${isUpdatingEvent ? 'Updated' : 'Created'} event entry in admin events page.`,
      });
    } catch (err) {
      console.error('Save event error:', err);
      setError(err.message || 'Could not save event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Saves the current event form state as a local draft in localStorage without
  // publishing it to the database — useful for incomplete events. ("drafts")
  const saveEventDraft = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setPublishConfirm(null);
    setStatusMessage('');
    const draftEntry = {
      id: `draft-event-${Date.now()}`,
      title: form.title?.trim() || 'Untitled event',
      description: form.description?.trim() || '',
      event_date: form.event_date || '',
      start_time: form.start_time || '',
      end_time: form.end_time || '',
      location: form.location?.trim() || '',
      category: form.category || 'community',
      event_url: normalizeWebsiteUrl(form.event_url),
      created_at: new Date().toISOString(),
      status: 'Draft',
    };
    const updatedDrafts = [draftEntry, ...eventDrafts.filter((draft) => draft.id !== draftEntry.id)];
    saveStoredDrafts('clipstone_event_drafts', updatedDrafts);
    setEventDrafts(updatedDrafts);
    resetForm();
    setStatusMessage('Event saved as a draft locally.');
  };

  // ── Load event into form for editing ─────────────────────────────────────────
  // Populates the event form with an existing event's data so the admin can edit and republish. ("events")
  const startEdit = (event) => {
    setEditingId(event.id);
    setForm({
      title:       event.title       || '',
      description: event.description || '',
      event_date:  event.event_date  || '',
      start_time:  event.start_time  || '',
      end_time:    event.end_time    || '',
      location:    event.location    || '',
      category:    event.category    || 'community',
      event_url: event.event_url || eventLinkMap[event.id] || '',
    });
    setImagePreview(event.image_url || null);
    setImageFile(null);
    setError('');
    setStatusMessage('');
  };

  // ── Delete event ──────────────────────────────────────────────────────────────
  // Permanently removes the event from tbl_events via Supabase and records an audit log entry. ("events")
  const handleDelete = async (id) => {
    const eventToDelete = events.find((event) => event.id === id);
    const { error: deleteError } = await supabase
      .from('tbl_events')
      .delete()
      .eq('id', id);

    if (deleteError) {
      console.error(deleteError);
      setError('Could not delete event.');
    } else {
      setDeleteConfirmId(null);
      await fetchEvents();
      appendAuditLogEntry({
        actorEmail: adminEmail,
        action: 'Deleted',
        entityType: 'event',
        entityName: eventToDelete?.title || 'Event',
        details: 'Deleted event from admin events page.',
      });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Header */}
        <div style={styles.pageHeader}>
          <div>
            <h2 style={styles.title}>
              {editingId ? <><i className="icon-calendar" style={styles.actionIcon} /> Edit Event</> : <><i className="icon-plus" style={styles.actionIcon} /> Add New Event</>}
            </h2>
            <p style={styles.pageSubtitle}>Clipstone Miners Welfare — Admin Panel</p>
          </div>
          <button style={styles.backBtn} onClick={() => navigate('/admin-account')}>
            <i className="icon-left" style={styles.actionIcon} /> Dashboard
          </button>
        </div>

        {/* Form */}
        <div style={styles.formCard}>
          {error && <div style={styles.errorAlert}><i className="icon-left-circled" style={styles.actionIcon} /> {error}</div>}
          {statusMessage && <div style={styles.statusBanner}>{statusMessage}</div>}
          <div style={styles.socialUpcomingCallout}>
            <div>
              <h4 style={styles.socialUpcomingTitle}>Upcoming Social Events</h4>
              <p style={styles.socialUpcomingText}>
                Use this section to publish events that appear on the Social page and accept attendance requests under Social bookings.
              </p>
              <p style={styles.socialUpcomingText}>
                Currently scheduled: <strong>{upcomingSocialEvents.length}</strong> upcoming social event{upcomingSocialEvents.length === 1 ? '' : 's'}.
              </p>
            </div>
            <button
              type="button"
              onClick={startSocialUpcomingEvent}
              style={styles.socialUpcomingBtn}
            >
              <i className="icon-plus" style={styles.actionIcon} />
              Add social upcoming event
            </button>
          </div>
          {upcomingSocialEvents.length > 0 && (
            <div style={styles.socialUpcomingList}>
              {upcomingSocialEvents.slice(0, 5).map((event) => (
                <div key={event.id} style={styles.socialUpcomingItem}>
                  <span style={styles.socialUpcomingItemTitle}>{event.title}</span>
                  <span style={styles.socialUpcomingItemMeta}>
                    {event.event_date}
                    {event.start_time ? ` · ${event.start_time}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.form} noValidate>

            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Event Title *</label>
                <input
                  placeholder="e.g. Community Bingo Night"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  style={styles.input}
                >
                  {CATEGORIES.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
                {form.category === 'social' && (
                  <p style={styles.hint}>This event will be listed in Upcoming Social Events and can receive attendance requests.</p>
                )}
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                placeholder="Describe the event..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                style={{ ...styles.input, minHeight: '90px', resize: 'vertical' }}
              />
            </div>

            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Date *</label>
                <input
                  type="date"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Start Time</label>
                <input
                  type="time"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>End Time</label>
                <input
                  type="time"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Location</label>
                <select
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  style={styles.input}
                >
                  <option value="">-- Select a location --</option>
                  <option value="Mansfield Road, Clipstone NG21 9AL">
                    Clipstone Miners Welfare — Mansfield Road, Clipstone NG21 9AL
                  </option>
                  <option value="Clipstone Sports Field, Clipstone NG21 9AL">
                    Clipstone Sports Field — Clipstone NG21 9AL
                  </option>
                </select>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Event website link</label>
              <input
                type="url"
                placeholder="e.g. https://example.com/events"
                value={form.event_url}
                onChange={(e) => setForm({ ...form, event_url: e.target.value })}
                style={styles.input}
              />
              <p style={styles.hint}>Optional public link for this event listing.</p>
            </div>

            {/* Image upload */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Event Image</label>
              <div style={styles.imageUploadRow}>
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={styles.imagePreview}
                  />
                )}
                <label style={styles.uploadBtn}>
                  {imagePreview ? <><i className="icon-camera" style={styles.actionIcon} /> Change Image</> : <><i className="icon-camera" style={styles.actionIcon} /> Upload Image</>}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    style={{ display: 'none' }}
                  />
                </label>
                {imagePreview && (
                  imageDeleteConfirm === 'event' ? (
                    <div style={styles.inlineDeleteConfirmWrap}>
                      <span style={styles.inlineDeleteConfirmText}>Remove image?</span>
                      <button
                        type="button"
                        style={styles.inlineDeleteConfirmBtn}
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview(null);
                          setImageDeleteConfirm(null);
                        }}
                      >
                        Yes
                      </button>
                      <button type="button" style={styles.inlineDeleteCancelBtn} onClick={() => setImageDeleteConfirm(null)}>No</button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      style={styles.removeImageBtn}
                      onClick={() => setImageDeleteConfirm('event')}
                    >
                      <i className="icon-left-circled" style={styles.actionIcon} /> Remove
                    </button>
                  )
                )}
              </div>
              <p style={styles.hint}>JPG, PNG, WEBP or GIF · Max 5MB</p>
            </div>

            {/* Form buttons */}
            <div style={styles.formBtnRow}>
              <button
                type="submit"
                disabled={saving}
                style={saving
                  ? { ...styles.saveBtn, opacity: 0.6, cursor: 'not-allowed' }
                  : styles.saveBtn}
              >
                {saving
                  ? 'Saving...'
                  : editingId
                    ? <><i className="icon-calendar" style={styles.actionIcon} /> Publish update</>
                    : form.category === 'social'
                      ? <><i className="icon-plus" style={styles.actionIcon} /> Publish social upcoming event</>
                      : <><i className="icon-plus" style={styles.actionIcon} /> Publish event</>}
              </button>
              <button
                type="button"
                onClick={saveEventDraft}
                style={styles.draftBtn}
              >
                <i className="icon-book" style={styles.actionIcon} /> Save draft
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  style={styles.cancelBtn}
                >
                  Cancel
                </button>
              )}
            </div>

          </form>
        </div>

        <div style={styles.draftsCard}>
          <div style={styles.requestsHeader}>
            <h3 style={styles.sectionTitle}>Drafts</h3>
            <p style={styles.mutedText}>Open a saved draft, edit it, publish it, or delete it.</p>
          </div>

          <div style={styles.draftToggleRow}>
            <button
              type="button"
              onClick={() => setActiveDraftSection('event')}
              style={activeDraftSection === 'event' ? styles.draftToggleBtnActive : styles.draftToggleBtn}
            >
              Event drafts ({eventDrafts.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveDraftSection('content')}
              style={activeDraftSection === 'content' ? styles.draftToggleBtnActive : styles.draftToggleBtn}
            >
              Activity drafts ({contentDrafts.length})
            </button>
          </div>

          {activeDraftSection === 'event' ? (
            eventDrafts.length === 0 ? (
              <p style={styles.mutedText}>No saved event drafts yet.</p>
            ) : (
              <div style={styles.draftList}>
                {eventDrafts.map((draft) => (
                  <div key={draft.id} style={styles.draftItem}>
                    <div>
                      <div style={styles.requestTitle}>{draft.title || 'Untitled event'}</div>
                      <div style={styles.requestMeta}>
                        {draft.event_date ? `Date: ${draft.event_date}` : 'Date not set'}
                        {draft.category ? ` · ${draft.category}` : ''}
                      </div>
                      <div style={styles.requestMeta}>Saved: {new Date(draft.created_at || Date.now()).toLocaleString('en-GB')}</div>
                    </div>
                    <div style={styles.draftItemActions}>
                      <button type="button" style={styles.draftLoadBtn} onClick={() => loadEventDraftIntoForm(draft)}>Load into form</button>
                      {draftDeleteConfirm?.type === 'event' && draftDeleteConfirm?.id === draft.id ? (
                        <div style={styles.inlineDeleteConfirmWrap}>
                          <span style={styles.inlineDeleteConfirmText}>Delete draft?</span>
                          <button type="button" style={styles.inlineDeleteConfirmBtn} onClick={() => deleteStoredEventDraft(draft.id)}>Yes</button>
                          <button type="button" style={styles.inlineDeleteCancelBtn} onClick={() => setDraftDeleteConfirm(null)}>No</button>
                        </div>
                      ) : (
                        <button type="button" style={styles.draftDeleteBtn} onClick={() => setDraftDeleteConfirm({ type: 'event', id: draft.id })}>Delete</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : contentDrafts.length === 0 ? (
            <p style={styles.mutedText}>No saved activity drafts yet.</p>
          ) : (
            <div style={styles.draftList}>
              {contentDrafts.map((draft) => (
                <div key={draft.id} style={styles.draftItem}>
                  <div>
                    <div style={styles.requestTitle}>{draft.title || 'Untitled activity'}</div>
                    <div style={styles.requestMeta}>{getCategoryLabel(draft.category || 'community')}</div>
                    <div style={styles.requestMeta}>Saved: {new Date(draft.created_at || Date.now()).toLocaleString('en-GB')}</div>
                  </div>
                  <div style={styles.draftItemActions}>
                    <button type="button" style={styles.draftLoadBtn} onClick={() => loadContentDraftIntoForm(draft)}>Load into form</button>
                    {draftDeleteConfirm?.type === 'content' && draftDeleteConfirm?.id === draft.id ? (
                      <div style={styles.inlineDeleteConfirmWrap}>
                        <span style={styles.inlineDeleteConfirmText}>Delete draft?</span>
                        <button type="button" style={styles.inlineDeleteConfirmBtn} onClick={() => deleteStoredContentDraft(draft.id)}>Yes</button>
                        <button type="button" style={styles.inlineDeleteCancelBtn} onClick={() => setDraftDeleteConfirm(null)}>No</button>
                      </div>
                    ) : (
                      <button type="button" style={styles.draftDeleteBtn} onClick={() => setDraftDeleteConfirm({ type: 'content', id: draft.id })}>Delete</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Booking requests */}
        <div style={styles.requestsCard}>
          <div style={styles.requestsHeader}>
            <h3 style={styles.sectionTitle}>
              Booking Requests
              <span style={styles.eventCount}>{visibleBookingRequests.length}</span>
            </h3>
            <p style={styles.mutedText}>Review and manage requests from trust and social bookings.</p>
          </div>

          <div style={styles.draftToggleRow}>
            <button
              type="button"
              onClick={() => setActiveBookingSection('trust')}
              style={activeBookingSection === 'trust' ? styles.draftToggleBtnActive : styles.draftToggleBtn}
            >
              Trust bookings ({trustBookingRequests.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveBookingSection('social-events')}
              style={activeBookingSection === 'social-events' ? styles.draftToggleBtnActive : styles.draftToggleBtn}
            >
              Social bookings ({socialEventRequests.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveBookingSection('venue')}
              style={activeBookingSection === 'venue' ? styles.enquiryToggleBtnActive : styles.enquiryToggleBtn}
            >
              ✉ Venue Bookings & Enquiries
              {venueRequests.length > 0 && (
                <span style={styles.enquiryCount}>{venueRequests.length}</span>
              )}
            </button>
          </div>

          {bookingLoading ? (
            <p style={styles.mutedText}>Loading booking requests...</p>
          ) : visibleBookingRequests.length === 0 ? (
            <p style={styles.mutedText}>
              {activeBookingSection === 'venue'
                ? 'No venue bookings or enquiries yet.'
                : activeBookingSection === 'social-events'
                  ? 'No social event booking requests yet.'
                  : 'No trust booking requests yet.'}
            </p>
          ) : (
            <div style={styles.requestList}>
              {visibleBookingRequests.map((request) => {
                const isSelected = selectedRequestId === request.id;
                const expiryDate = new Date(request.created_at);
                expiryDate.setDate(expiryDate.getDate() + AUTO_EXPIRE_DAYS);
                const isKept = getKeptBookingRequestIds().includes(request.id);
                const { receipt, cleanNotes } = getRentalReceiptMeta(request.notes);

                return (
                  <div key={request.id} style={styles.requestItem}>
                    <div style={styles.requestSummary}>
                      <div>
                        <div style={styles.requestTitle}>{request.full_name}</div>
                        <div style={styles.requestMeta}>{request.group_name} • {request.event_title}</div>
                      </div>
                      <div style={styles.requestActions}>
                        <span style={styles.requestBadge}>{request.status || 'pending'}</span>
                          {activeBookingSection === 'venue' && (
                            <span style={request.booking_type === 'pre-enquiry' ? styles.enquiryTypeBadge : styles.roomHireTypeBadge}>
                              {request.booking_type === 'pre-enquiry' ? 'Enquiry' : 'Room Hire'}
                            </span>
                          )}
                          {activeBookingSection === 'social-events' && (
                            <span style={styles.socialEventTypeBadge}>Social Event</span>
                          )}
                        <button style={styles.requestToggleBtn} onClick={() => setSelectedRequestId(isSelected ? null : request.id)}>
                          {isSelected ? 'Hide details' : 'View details'}
                        </button>
                        <button style={{ ...styles.requestToggleBtn, color: isKept ? '#0f766e' : '#374151' }} onClick={() => toggleKeepBookingRequest(request.id)}>
                          {isKept ? 'Keep active' : 'Keep'}
                        </button>
                        {bookingDeleteId === request.id ? (
                          <div style={styles.deleteConfirm}>
                            <p style={styles.deleteConfirmText}>Delete this booking request?</p>
                            <div style={styles.deleteConfirmBtns}>
                              <button style={styles.cancelDeleteBtn} onClick={() => setBookingDeleteId(null)}>Cancel</button>
                              <button style={styles.confirmDeleteBtn} onClick={() => deleteBookingRequest(request.id)}>Yes, Delete</button>
                            </div>
                          </div>
                        ) : (
                          <button style={{ ...styles.requestToggleBtn, color: '#dc2626' }} onClick={() => setBookingDeleteId(request.id)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <div style={styles.requestDetails}>
                        <div><strong>Email:</strong> {request.email}</div>
                        <div><strong>Phone:</strong> {request.phone || '—'}</div>
                        <div><strong>Attendees:</strong> {request.attendees}</div>
                        <div><strong>Preferred date:</strong> {request.preferred_date || 'Not provided'}</div>
                        {receipt && (
                          <>
                            <div><strong>Receipt number:</strong> {receipt.receiptNumber || '—'}</div>
                            <div><strong>Rental suite:</strong> {receipt.suiteName || '—'}</div>
                            <div><strong>Payment method:</strong> {receipt.payment?.method || '—'}</div>
                            <div><strong>Payment reference:</strong> {receipt.payment?.reference || '—'}</div>
                            <div><strong>Payment status:</strong> {receipt.payment?.status || '—'}</div>
                            <div>
                              <strong>Estimated total:</strong>{' '}
                              {typeof receipt.costs?.total === 'number'
                                ? `GBP ${receipt.costs.total.toFixed(2)}`
                                : '—'}
                            </div>
                          </>
                        )}
                        {request.booking_type === 'pre-enquiry' && cleanNotes ? (
                          <div style={styles.enquiryMessageBlock}>
                            <div style={styles.enquiryMessageLabel}>Message from enquirer</div>
                            <div style={styles.enquiryMessageText}>{cleanNotes}</div>
                          </div>
                        ) : (
                          <div><strong>Notes:</strong> {cleanNotes || 'No additional notes.'}</div>
                        )}
                        <div><strong>Submitted:</strong> {new Date(request.created_at).toLocaleString('en-GB')}</div>
                        <div><strong>Auto-delete:</strong> {isKept ? 'Disabled by admin' : `${expiryDate.toLocaleDateString('en-GB')} (${AUTO_EXPIRE_DAYS} days)`}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Category content manager */}
        <div style={styles.requestsCard}>
          <div style={styles.requestsHeader}>
            <h3 style={styles.sectionTitle}>Category Content Manager</h3>
            <p style={styles.mutedText}>Add, edit, or remove activity information shown on each subgroup page.</p>
          </div>

          <form onSubmit={promptCategoryContentPublish} style={styles.form} noValidate>
            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Section</label>
                <select value={contentForm.category} onChange={(e) => setContentForm({ ...contentForm, category: e.target.value })} style={styles.input}>
                  {CATEGORIES.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Status</label>
                <select value={contentForm.status} onChange={(e) => setContentForm({ ...contentForm, status: e.target.value })} style={styles.input}>
                  <option value="Hosted">Hosted</option>
                  <option value="Not currently hosted">Not currently hosted</option>
                </select>
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Title</label>
              <input value={contentForm.title} onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })} style={styles.input} placeholder="e.g. Weekly wellbeing club" />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Short description</label>
              <textarea value={contentForm.description} onChange={(e) => setContentForm({ ...contentForm, description: e.target.value })} style={{ ...styles.input, minHeight: '70px', resize: 'vertical' }} placeholder="Add a short summary for this activity" />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Details</label>
              <textarea value={contentForm.details} onChange={(e) => setContentForm({ ...contentForm, details: e.target.value })} style={{ ...styles.input, minHeight: '90px', resize: 'vertical' }} placeholder="Add more information about dates, accessibility, or availability" />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Activity website link</label>
              <input
                type="url"
                value={contentForm.website_url}
                onChange={(e) => setContentForm({ ...contentForm, website_url: e.target.value })}
                style={styles.input}
                placeholder="e.g. https://group-website.example"
              />
              <p style={styles.hint}>Optional link shown in activity information on the public page.</p>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Trustee link</label>
              <select value={contentForm.trustee_id} onChange={(e) => setContentForm({ ...contentForm, trustee_id: e.target.value })} style={styles.input}>
                <option value="">No trustee linked</option>
                {trustees.map((trustee) => (
                  <option key={trustee.id} value={trustee.id}>{trustee.name}</option>
                ))}
              </select>
              <p style={styles.hint}>Link this section to a trustee so it can be shown in the admin dashboard.</p>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Images</label>
              <div style={styles.imageUploadRow}>
                {contentImagePreviews.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {contentImagePreviews.map((preview, index) => (
                      <img key={`${preview}-${index}`} src={preview} alt={`Content preview ${index + 1}`} style={styles.imagePreview} />
                    ))}
                  </div>
                )}
                <label style={styles.uploadBtn}>
                  <><i className="icon-camera" style={styles.actionIcon} /> {contentImagePreviews.length > 0 ? 'Add more images' : 'Upload images'}</>
                  <input type="file" accept="image/*" multiple onChange={handleContentImageSelect} style={{ display: 'none' }} />
                </label>
                {contentImagePreviews.length > 0 && (
                  imageDeleteConfirm === 'content' ? (
                    <div style={styles.inlineDeleteConfirmWrap}>
                      <span style={styles.inlineDeleteConfirmText}>Remove all images?</span>
                      <button
                        type="button"
                        style={styles.inlineDeleteConfirmBtn}
                        onClick={() => {
                          setContentImageFiles([]);
                          setContentImagePreviews([]);
                          setContentForm({ ...contentForm, image_url: '', image_urls: [] });
                          setImageDeleteConfirm(null);
                        }}
                      >
                        Yes
                      </button>
                      <button type="button" style={styles.inlineDeleteCancelBtn} onClick={() => setImageDeleteConfirm(null)}>No</button>
                    </div>
                  ) : (
                    <button type="button" style={styles.removeImageBtn} onClick={() => setImageDeleteConfirm('content')}>
                      <i className="icon-left-circled" style={styles.actionIcon} /> Remove all
                    </button>
                  )
                )}
              </div>
              <p style={styles.hint}>Optional images for this activity card · JPG, PNG, WEBP or GIF · max 5MB each</p>
            </div>

            <div style={styles.formBtnRow}>
              <button type="submit" style={styles.saveBtn}>{contentEditingId ? 'Publish update' : 'Add activity info'}</button>
              <button type="button" onClick={saveCategoryContentDraft} style={styles.draftBtn}>Save draft</button>
              {contentEditingId && <button type="button" onClick={resetContentForm} style={styles.cancelBtn}>Cancel</button>}
            </div>
          </form>

          {contentLoading ? (
            <p style={styles.mutedText}>Loading content items...</p>
          ) : contentItems.length === 0 ? (
            <p style={styles.mutedText}>No content items yet.</p>
          ) : (
            <div style={styles.requestList}>
              {contentItems.map((item) => (
                <div key={item.id} style={styles.requestItem}>
                  {(() => {
                    const itemImages = Array.isArray(item.image_urls) && item.image_urls.length > 0
                      ? item.image_urls
                      : item.image_url
                        ? [item.image_url]
                        : [];
                    const primaryImage = itemImages[0] || null;
                    return (
                  <div style={styles.requestSummary}>
                    <div style={styles.contentItemMain}>
                      <div style={styles.contentImageBox}>
                        {primaryImage ? (
                          <img src={primaryImage} alt={item.title} style={styles.contentImagePreview} />
                        ) : (
                          <div style={styles.contentImagePlaceholder}>No image</div>
                        )}
                      </div>
                      <div style={styles.contentItemText}>
                        <div style={styles.requestTitle}>{item.title}</div>
                        <div style={styles.requestMeta}>{getCategoryLabel(item.category)} • {item.status}{itemImages.length > 1 ? ` • ${itemImages.length} images` : ''}</div>
                        {(item.website_url || categoryLinkMap[item.id]) && (
                          <a href={item.website_url || categoryLinkMap[item.id]} target="_blank" rel="noreferrer" style={styles.inlineLink}>
                            Open linked activity website
                          </a>
                        )}
                        {item.trustee_id && (
                          <div style={styles.requestMeta}>Linked trustee: {trustees.find((trustee) => trustee.id === item.trustee_id)?.name || 'Unknown'}</div>
                        )}
                      </div>
                    </div>
                    <div style={styles.requestActions}>
                      <button style={styles.requestToggleBtn} onClick={() => startEditContent(item)}>Edit</button>
                      {contentDeleteId === item.id ? (
                        <div style={styles.deleteConfirm}>
                          <p style={styles.deleteConfirmText}>Delete this content item?</p>
                          <div style={styles.deleteConfirmBtns}>
                            <button style={styles.cancelDeleteBtn} onClick={() => setContentDeleteId(null)}>Cancel</button>
                            <button style={styles.confirmDeleteBtn} onClick={() => deleteCategoryContent(item.id)}>Yes, Delete</button>
                          </div>
                        </div>
                      ) : (
                        <button style={{ ...styles.requestToggleBtn, color: '#dc2626' }} onClick={() => setContentDeleteId(item.id)}>Delete</button>
                      )}
                    </div>
                  </div>
                    );
                  })()}
                  {item.description && <div style={styles.requestDetails}>{item.description}</div>}
                  {(Array.isArray(item.image_urls) ? item.image_urls : (item.image_url ? [item.image_url] : [])).length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                      {(Array.isArray(item.image_urls) ? item.image_urls : (item.image_url ? [item.image_url] : [])).map((image, index) => (
                        <img
                          key={`${item.id}-image-${index}`}
                          src={image}
                          alt={`${item.title} ${index + 1}`}
                          style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        />
                      ))}
                    </div>
                  )}
                  {item.details && <div style={styles.requestDetails}>{item.details}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Events list */}
        <h3 style={styles.sectionTitle}>
          All Events
          <span style={styles.eventCount}>{events.length}</span>
        </h3>

        {loading ? (
          <p style={styles.mutedText}>Loading events...</p>
        ) : events.length === 0 ? (
          <p style={styles.mutedText}>No events yet — add one above.</p>
        ) : (
          <div style={styles.eventGrid}>
            {events.map((event) => (
              <div key={event.id} style={styles.eventCard}>

                {event.image_url && (
                  <img
                    src={event.image_url}
                    alt={event.title}
                    style={styles.cardImage}
                  />
                )}

                <div style={styles.cardBody}>
                  <span style={styles.categoryTag}>
                    {getCategoryLabel(event.category) || 'General'}
                  </span>
                  <h4 style={styles.cardTitle}>{event.title}</h4>
                  <p style={styles.cardMeta}>
                    <i className="icon-calendar" style={styles.metaIcon} /> {event.event_date}
                    {event.start_time && ` · `}<i className="icon-clock" style={styles.metaIcon} />{event.start_time && ` ${event.start_time}`}
                    {event.location && ` · `}<i className="icon-location" style={styles.metaIcon} />{event.location && ` ${event.location}`}
                  </p>
                  {event.description && (
                    <p style={styles.cardDesc}>{event.description}</p>
                  )}
                  {(event.event_url || eventLinkMap[event.id]) && (
                    <a href={event.event_url || eventLinkMap[event.id]} target="_blank" rel="noreferrer" style={styles.inlineLink}>
                      Visit event website
                    </a>
                  )}
                </div>

                {/* Delete confirmation inline */}
                {deleteConfirmId === event.id ? (
                  <div style={styles.deleteConfirm}>
                    <p style={styles.deleteConfirmText}>Delete this event?</p>
                    <div style={styles.deleteConfirmBtns}>
                      <button
                        style={styles.confirmDeleteBtn}
                        onClick={() => handleDelete(event.id)}
                      >
                        Yes, Delete
                      </button>
                      <button
                        style={styles.cancelDeleteBtn}
                        onClick={() => setDeleteConfirmId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={styles.cardActions}>
                    <button
                      style={styles.editBtn}
                      onClick={() => startEdit(event)}
                    >
                      <i className="icon-calendar" style={styles.actionIcon} /> Edit
                    </button>
                    <button
                      style={styles.deleteBtn}
                      onClick={() => setDeleteConfirmId(event.id)}
                    >
                      <i className="icon-left" style={styles.actionIcon} /> Delete
                    </button>
                  </div>
                )}

              </div>
            ))}
          </div>
        )}

      </div>

      {publishConfirm && (
        <div style={styles.confirmOverlay}>
          <div style={styles.confirmModal}>
            <h4 style={styles.confirmTitle}>
              {publishConfirm.type === 'event' ? 'Publish this event?' : 'Publish this activity info?'}
            </h4>
            <p style={styles.confirmText}>
              {publishConfirm.type === 'event'
                ? 'This will add the event to the public website immediately. You can also save it as a draft first.'
                : 'This will add the activity information to the public site immediately. You can also save it as a draft first.'}
            </p>
            <div style={styles.confirmActions}>
              <button type="button" onClick={publishConfirm.type === 'event' ? publishEvent : saveCategoryContent} style={styles.confirmPrimaryBtn}>
                {publishConfirm.type === 'event' ? 'Publish event' : 'Publish activity info'}
              </button>
              <button type="button" onClick={publishConfirm.type === 'event' ? saveEventDraft : saveCategoryContentDraft} style={styles.confirmDraftBtn}>
                Save as draft
              </button>
              <button type="button" onClick={() => setPublishConfirm(null)} style={styles.confirmCancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f9f8f6',
    padding: 'clamp(16px, 4vw, 40px) clamp(12px, 3vw, 20px)',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
  container: {
    maxWidth: '960px',
    margin: '0 auto',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '28px',
  },
  title: {
    color: '#1e3f32',
    margin: '0 0 4px 0',
    fontSize: '24px',
    fontWeight: '700',
  },
  pageSubtitle: {
    color: '#6b7280',
    fontSize: '14px',
    margin: 0,
  },
  backBtn: {
    background: 'transparent',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    padding: '10px 16px',
    color: '#374151',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600',
  },
  actionIcon: {
    display: 'inline-block',
    fontSize: '1.05em',
    marginRight: '8px',
    verticalAlign: 'middle',
  },
  metaIcon: {
    marginRight: '6px',
    fontSize: '1em',
    verticalAlign: 'middle',
  },
  formCard: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: 'clamp(16px, 3vw, 28px)',
    marginBottom: '36px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  errorAlert: {
    background: '#fff0f0',
    color: '#dc2626',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: 1,
    minWidth: '160px',
  },
  label: {
    color: '#374151',
    fontSize: '14px',
    fontWeight: '500',
  },
  input: {
    padding: '12px 14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '15px',
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    background: '#fafafa',
  },
  imageUploadRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  imagePreview: {
    width: '100px',
    height: '70px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  contentItemMain: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  contentItemText: {
    flex: 1,
    minWidth: 0,
  },
  contentImageBox: {
    width: '64px',
    height: '64px',
    borderRadius: '10px',
    border: '1px solid #d9e2dc',
    background: '#f7fbf8',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  contentImagePreview: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  contentImagePlaceholder: {
    color: '#6b7280',
    fontSize: '10px',
    textAlign: 'center',
    padding: '6px',
    lineHeight: 1.3,
  },
  uploadBtn: {
    background: '#1e3f32',
    color: '#fff',
    padding: '10px 18px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-block',
  },
  removeImageBtn: {
    background: 'transparent',
    color: '#dc2626',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  hint: {
    color: '#9ca3af',
    fontSize: '12px',
    margin: '4px 0 0 0',
  },
  socialUpcomingCallout: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    border: '1px solid #bfdbfe',
    background: '#eff6ff',
    borderRadius: '10px',
    padding: '12px 14px',
    marginBottom: '16px',
  },
  socialUpcomingTitle: {
    margin: '0 0 4px',
    color: '#1e3a8a',
    fontSize: '15px',
    fontWeight: '700',
  },
  socialUpcomingText: {
    margin: '0',
    color: '#1f2937',
    fontSize: '13px',
    lineHeight: '1.5',
  },
  socialUpcomingBtn: {
    background: '#1e3f32',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  socialUpcomingList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px',
  },
  socialUpcomingItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
    flexWrap: 'wrap',
    border: '1px solid #dbeafe',
    background: '#f8fbff',
    borderRadius: '8px',
    padding: '8px 12px',
  },
  socialUpcomingItemTitle: {
    color: '#1e3f32',
    fontSize: '13px',
    fontWeight: '700',
  },
  socialUpcomingItemMeta: {
    color: '#475569',
    fontSize: '12px',
    fontWeight: '600',
  },
  inlineLink: {
    display: 'inline-block',
    marginTop: '6px',
    color: '#0f766e',
    fontSize: '13px',
    fontWeight: '700',
    textDecoration: 'none',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  formBtnRow: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    marginTop: '4px',
  },
  statusBanner: {
    position: 'fixed',
    top: '12px',
    right: '12px',
    zIndex: 2500,
    maxWidth: 'min(320px, calc(100vw - 24px))',
    background: '#f0fdf4',
    color: '#166534',
    border: '1px solid #86efac',
    borderRadius: '10px',
    padding: '12px 14px',
    fontSize: '13px',
    fontWeight: '700',
    boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
  },
  saveBtn: {
    background: '#1e3f32',
    color: '#fff',
    padding: '14px 28px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  draftBtn: {
    background: '#f8fafc',
    color: '#1f2937',
    padding: '14px 20px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  cancelBtn: {
    background: 'transparent',
    color: '#6b7280',
    padding: '14px 20px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '15px',
    cursor: 'pointer',
  },
  draftsCard: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: 'clamp(14px, 3vw, 24px) clamp(14px, 3vw, 24px) 20px',
    marginBottom: '24px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
  },
  draftToggleRow: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  draftToggleBtn: {
    background: '#f8fafc',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '999px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  draftToggleBtnActive: {
    background: '#1e3f32',
    color: '#fff',
    border: '1px solid #1e3f32',
    borderRadius: '999px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  draftList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  draftItem: {
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '12px 14px',
    background: '#fafafa',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  draftItemActions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  draftLoadBtn: {
    background: '#1e3f32',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  draftDeleteBtn: {
    background: 'transparent',
    color: '#dc2626',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  inlineDeleteConfirmWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#fff0f0',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '6px 8px',
  },
  inlineDeleteConfirmText: {
    color: '#991b1b',
    fontSize: '12px',
    fontWeight: '600',
  },
  inlineDeleteConfirmBtn: {
    border: 'none',
    background: '#dc2626',
    color: '#fff',
    borderRadius: '6px',
    padding: '5px 8px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  inlineDeleteCancelBtn: {
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#4b5563',
    borderRadius: '6px',
    padding: '5px 8px',
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  requestsCard: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: 'clamp(14px, 3vw, 24px) clamp(14px, 3vw, 24px) 20px',
    marginBottom: '24px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
  },
  requestsHeader: {
    marginBottom: '16px',
  },
  requestList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  requestItem: {
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '14px 16px',
    background: '#fafafa',
    overflow: 'hidden',
  },
  requestSummary: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    flexWrap: 'wrap',
  },
  requestTitle: {
    color: '#1e3f32',
    fontSize: '15px',
    fontWeight: '700',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  requestMeta: {
    color: '#6b7280',
    fontSize: '13px',
    marginTop: '4px',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  requestActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    alignSelf: 'flex-start',
  },
  requestBadge: {
    background: '#ecfdf5',
    color: '#047857',
    borderRadius: '999px',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  requestToggleBtn: {
    background: 'transparent',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    padding: '7px 10px',
    color: '#1e3f32',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
  },
  requestDetails: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #e5e7eb',
    color: '#374151',
    fontSize: '13px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  sectionTitle: {
    color: '#1e3f32',
    fontSize: '18px',
    fontWeight: '700',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  eventCount: {
    background: '#1e3f32',
    color: '#fff',
    borderRadius: '20px',
    padding: '2px 10px',
    fontSize: '13px',
    fontWeight: '700',
  },
  mutedText: {
    color: '#9ca3af',
    fontSize: '15px',
  },
  eventGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  eventCard: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
  },
  cardImage: {
    width: '100%',
    height: '150px',
    objectFit: 'cover',
  },
  cardBody: {
    padding: '14px 16px',
  },
  categoryTag: {
    display: 'inline-block',
    background: '#ecfdf5',
    color: '#059669',
    fontSize: '11px',
    fontWeight: '700',
    padding: '3px 10px',
    borderRadius: '20px',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  cardTitle: {
    color: '#1e3f32',
    fontSize: '16px',
    fontWeight: '700',
    margin: '0 0 6px 0',
  },
  cardMeta: {
    color: '#6b7280',
    fontSize: '12px',
    margin: '0 0 8px 0',
  },
  cardDesc: {
    color: '#374151',
    fontSize: '13px',
    margin: 0,
    lineHeight: '1.5',
  },
  deleteConfirm: {
    padding: '14px 16px',
    background: '#fff0f0',
    borderTop: '1px solid #fca5a5',
  },
  deleteConfirmText: {
    color: '#dc2626',
    fontSize: '13px',
    fontWeight: '600',
    margin: '0 0 10px 0',
  },
  deleteConfirmBtns: {
    display: 'flex',
    gap: '8px',
  },
  confirmDeleteBtn: {
    flex: 1,
    padding: '8px',
    background: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  cancelDeleteBtn: {
    flex: 1,
    padding: '8px',
    background: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  cardActions: {
    display: 'flex',
    borderTop: '1px solid #f3f4f6',
  },
  editBtn: {
    flex: 1,
    padding: '12px',
    background: 'transparent',
    color: '#1e3f32',
    border: 'none',
    borderRight: '1px solid #f3f4f6',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  deleteBtn: {
    flex: 1,
    padding: '12px',
    background: 'transparent',
    color: '#dc2626',
    border: 'none',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  confirmOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(17, 24, 39, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 2000,
  },
  confirmModal: {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '480px',
    width: '100%',
    boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
  },
  confirmTitle: {
    margin: '0 0 8px 0',
    color: '#1e3f32',
    fontSize: '20px',
  },
  confirmText: {
    margin: '0 0 20px 0',
    color: '#4b5563',
    lineHeight: 1.6,
    fontSize: '15px',
  },
  confirmActions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  confirmPrimaryBtn: {
    background: '#1e3f32',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 16px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  confirmDraftBtn: {
    background: '#f8fafc',
    color: '#1f2937',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    padding: '10px 16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  confirmCancelBtn: {
    background: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    padding: '10px 16px',
    fontWeight: '600',
    cursor: 'pointer',
  },

  // ── Enquiry tab styles ────────────────────────────────────────────────────────
  enquiryToggleBtn: {
    background: '#fffbeb',
    color: '#92400e',
    border: '1px solid #fde68a',
    borderRadius: '999px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  enquiryToggleBtnActive: {
    background: '#d97706',
    color: '#ffffff',
    border: '1px solid #d97706',
    borderRadius: '999px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  enquiryCount: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    padding: '0 5px',
    borderRadius: '999px',
    background: '#ffffff',
    color: '#92400e',
    fontSize: '11px',
    fontWeight: '800',
  },
  enquiryTypeBadge: {
    background: '#fffbeb',
    color: '#92400e',
    border: '1px solid #fde68a',
    borderRadius: '999px',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  roomHireTypeBadge: {
    background: '#eff6ff',
    color: '#1e40af',
    border: '1px solid #bfdbfe',
    borderRadius: '999px',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  socialEventTypeBadge: {
    background: '#f0fdf4',
    color: '#166534',
    border: '1px solid #bbf7d0',
    borderRadius: '999px',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  enquiryMessageBlock: {
    marginTop: '4px',
    padding: '12px 14px',
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderLeft: '4px solid #d97706',
    borderRadius: '8px',
  },
  enquiryMessageLabel: {
    fontSize: '11px',
    fontWeight: '800',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#92400e',
    marginBottom: '6px',
  },
  enquiryMessageText: {
    fontSize: '13px',
    lineHeight: '1.65',
    color: '#374151',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
};

export default AdminEvents;